import React, { useState, useEffect, useRef } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, TextField, Button, IconButton, Typography, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Edit';
import { Authenticate, verifyOTP, initOTPless } from '../../utils/initOtpless.js';
import { loginSendOtpAction, verifyOtpAction } from '../.././store/actions/';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';

const OtpLogin = ({ open, onClose }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [otpSent, setOtpSent] = useState(false);
    const [timer, setTimer] = useState(60);
    const [otpExpired, setOtpExpired] = useState(false);
    const phoneNumberRef = useRef('');

    const { successData, error, errorInfo } = useSelector(state => state.loginSendOtpReducer);
    const { successData: verifyOtpData, error: verifyOtpError, errorInfo: verifyOtpErrorInfo } = useSelector(state => state.verifyOtpReducer);

    useEffect(() => {
        initOTPless();
    }, []);

    useEffect(() => {
        if (successData && phoneNumberRef.current) {
            handleAuthenticate(phoneNumberRef.current);
        }
        if (error) {
            toast.error(errorInfo?.message);
        }
    }, [successData, error]);

    const handleAuthenticate = async (phoneNumber) => {
        // Add validation to ensure phone number exists
        if (!phoneNumber) {
            console.error('Phone number is missing');
            return;
        }

        try {
            const response = await Authenticate({
                channel: 'PHONE',
                phone: phoneNumber.toString(), // Ensure phone is a string
                countryCode: '+91'
            });
            setOtpSent(true);
            setTimer(60);
            setOtpExpired(false);
            console.log('OTP sent successfully');
        } catch (error) {
            console.error('Error sending OTP:', error);
        }
    };

    const handleVerifyOtp = async () => {
        try {
            const response = await verifyOTP({ otp: formik.values.otp, channel: 'PHONE', phone: phoneNumberRef.current, countryCode: '+91' });
            if (response.success) {
                console.log(response.success);
                dispatch(verifyOtpAction({ mobile_no: phoneNumberRef.current, status: response.success }));
            } else {
                console.error('Invalid OTP');
                toast.error('Invalid OTP');
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            toast.error('Error verifying OTP');
        }
    };

    const handleResendOtp = () => {
        formik.handleSubmit();
    };

    const resetOtpState = () => {
        formik.resetForm();
        setOtpSent(false);
        setTimer(60);
        setOtpExpired(false);
    };

    useEffect(() => {
        let interval;
        if (otpSent && timer > 0) {
            interval = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1);
            }, 1000);
        } else if (timer === 0) {
            setOtpExpired(true);
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [otpSent, timer]);

    const formik = useFormik({
        initialValues: {
            phoneNumber: '',
            otp: ''
        },
        validationSchema: Yup.object({
            phoneNumber: Yup.string().required('Phone number is required'),
            otp: Yup.string().when('otpSent', {
                is: true,
                then: Yup.string().required('OTP is required')
            })
        }),
        onSubmit: (values) => {
            phoneNumberRef.current = values.phoneNumber;
            if (!otpSent) {
                dispatch(loginSendOtpAction({ mobile_no: values.phoneNumber }));
            } else {
                handleVerifyOtp();
            }
        }
    });

    useEffect(() => {
        if (verifyOtpData) {
            console.log(verifyOtpData);
            localStorage.setItem('userDetails', JSON.stringify(verifyOtpData?.response));
            onClose();
            toast.success('logged in successfully');
            const userDetails = JSON.parse(localStorage.getItem('userDetails'));
            if (userDetails?.role === 'admin') {
                navigate('/admin/dashboard');
            }
            else if (userDetails?.role === 'super_admin') {
                navigate('/superAdmin/dashboard');
            }
            else if (userDetails?.role === 'stock_manager') {
                navigate('/storemanager/stockmanagement');
            }
            else if (userDetails?.role === 'production_manager') {
                navigate('/productionmanager/dashboard');
            }
            else {
                navigate('/');
            }
            window.location.reload();
        }
        if (verifyOtpError) {
            toast.error(verifyOtpErrorInfo.message);
        }
    }, [verifyOtpData, verifyOtpError, verifyOtpErrorInfo]);

    return (
        <Dialog open={open} disableScrollLock={true} sx={{ '& .MuiDialog-paper': { width: '500px', borderRadius: '15px' } }}>
            <DialogTitle sx={{ textAlign: 'center', marginTop: '8%' }}>
                <Typography sx={{ color: "gray" }}>Welcome to</Typography>
                <Typography sx={{ fontSize: "20.41px", fontWeight: '400px', color: '#31303b', }}>Inventory ERP</Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '20px' }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="phoneNumber"
                        label="Phone Number"
                        type="text"
                        variant="outlined"
                        size='small'
                        sx={{ width: otpSent ? '250px' : { xs: '250px', sm: '350px' } }}
                        value={formik.values.phoneNumber}
                        disabled={otpSent}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                        helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                    />
                    {otpSent && <IconButton onClick={resetOtpState} sx={{ marginLeft: '4px', border: '1px solid', borderRadius: '4px', padding: '6px' }}>
                        <CreateIcon />
                    </IconButton>}
                </Box>
                {!otpSent && (
                    <Button onClick={formik.handleSubmit} sx={{ backgroundColor: '#1814F3', color: '#fff', width: { xs: '200px', sm: '350px' }, mb: 2 }}>
                        Send OTP
                    </Button>
                )}
                {otpSent && !otpExpired && (
                    <>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="otp"
                            label="OTP"
                            type="text"
                            variant="outlined"
                            size='small'
                            sx={{ width: { xs: '250px', sm: '350px' }, mb: '20px' }}
                            value={formik.values.otp}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.otp && Boolean(formik.errors.otp)}
                            helperText={formik.touched.otp && formik.errors.otp}
                        />
                        <Typography sx={{ color: 'gray', mb: 2 }}>
                            OTP expires in {timer} seconds
                        </Typography>
                        <Button onClick={formik.handleSubmit} sx={{ backgroundColor: '#1814F3', color: '#fff', width: { xs: '200px', sm: '350px' }, mb: 2 }}>
                            Verify
                        </Button>
                    </>
                )}
                {otpExpired && (
                    <Alert severity="warning" sx={{ width: { xs: '250px', sm: '350px' }, mb: 2 }}>
                        Your OTP has expired. <Button onClick={handleResendOtp} sx={{ color: '#FC903D' }}>Resend OTP</Button>
                    </Alert>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default OtpLogin;